<script>
  import { AccurateBeeswarm } from "accurate-beeswarm-plot";
  import { beeswarm } from "d3-beeswarm";
  import Toggle from "svelte-toggle";
  import data from "./data/candidates_2020.json";
  import {
    scaleLinear,
    min,
    max,
    extent,
    descending,
    ascending,
    range,
  } from "d3";
  import { fade } from "svelte/transition";
  import AxisSwarm from "./AxisSwarm.svelte";

  // Define chart dimensions
  let width;
  let height;

  const margins = {
    marginTop: 120,
    marginRight: 50,
    marginBottom: 50,
    marginLeft: 50,
  };
  // Chart dimensions will change upon resizing
  $: dimensions = {
    width,
    height,
    ...margins,
    boundedHeight: Math.max(
      height - margins.marginTop - margins.marginBottom,
      0
    ),
    boundedWidth: Math.max(width - margins.marginLeft - margins.marginRight, 0),
  };

  // Define state variables
  let hoveredData;
  let showNewcomers = false;

  // Define constants
  const swarmRadius = 10;
  const radius = 8;

  // Define accessors and scales
  const xAccessor = (d) => d.age;
  const genderAccessor = (d) => d.gender;

  const isNew = (d) => d.newcomer === "Y";
  const isMale = (d) => genderAccessor(d) === "M";
  const isFemale = (d) => genderAccessor(d) === "F";

  const xMin = min(data, xAccessor);
  const yMax = max(data, xAccessor);
  let xScale;
  $: {
    xScale = scaleLinear()
      .domain([xMin - 10, yMax + 5])
      .range([0, dimensions.boundedWidth]);

    xScale = xScale.unknown(xScale(xMin));
  }
  $: xAccessorScaled = (d) => xScale(xAccessor(d));

  // Process data
  const dataProcessed = data.sort((a, b) => descending(a.newcomer, b.newcomer));

  const maleData = dataProcessed.filter(isMale);
  const femaleData = dataProcessed.filter(isFemale);

  // Compute swarm positions
  $: swarmedMaleData = new AccurateBeeswarm(
    maleData,
    swarmRadius,
    xAccessorScaled
  )
    .oneSided()
    .calculateYPositions()
    .map((d) => ({
      ...d,
      y: d.y + swarmRadius,
    }));
  // $: swarmedMaleData = beeswarm()
  //   .data(maleData)
  //   .distributeOn(xAccessorScaled)
  //   .radius(swarmRadius)
  //   .orientation("horizontal")
  //   .side("negative")
  //   .arrange()
  // let swarmedMaleData;
  // $: {
  //   const X = maleData.map(xAccessorScaled);
  //   const Y = dodge(X, swarmRadius);

  //   swarmedMaleData = maleData.map((d, i) => ({
  //     x: X[i],
  //     y: Y[i] + swarmRadius,
  //     datum: d,
  //   }));
  // }

  $: swarmedFemaleData = new AccurateBeeswarm(
    femaleData,
    swarmRadius,
    xAccessorScaled
  )
    .oneSided()
    .calculateYPositions()
    .map((d) => ({
      ...d,
      y: -d.y - swarmRadius,
    }));
  // $: swarmedFemaleData = beeswarm()
  //   .data(femaleData)
  //   .distributeOn(xAccessorScaled)
  //   .radius(swarmRadius)
  //   .orientation("horizontal")
  //   .side("positive")
  //   .arrange()
  // let swarmedFemaleData;
  // $: {
  //   const X = femaleData.map(xAccessorScaled);
  //   const Y = dodge(X, swarmRadius);

  //   swarmedFemaleData = femaleData.map((d, i) => ({
  //     x: X[i],
  //     y: -Y[i] - swarmRadius,
  //     datum: d,
  //   }));
  // }
  $: swarmedData = [...swarmedMaleData, ...swarmedFemaleData];

  // Given an array of x-values and a separation radius, returns an array of y-values.
  function dodge(X, radius) {
    const Y = new Float64Array(X.length);
    const radius2 = radius ** 2;
    const epsilon = 1e-3;
    let head = null,
      tail = null;

    // Returns true if circle ⟨x,y⟩ intersects with any circle in the queue.
    function intersects(x, y) {
      let a = head;
      while (a) {
        const ai = a.index;
        if (radius2 - epsilon > (X[ai] - x) ** 2 + (Y[ai] - y) ** 2)
          return true;
        a = a.next;
      }
      return false;
    }

    // Place each circle sequentially.
    for (const bi of range(X.length).sort((i, j) => X[i] - X[j])) {
      // Remove circles from the queue that can’t intersect the new circle b.
      while (head && X[head.index] < X[bi] - radius2) head = head.next;

      // Choose the minimum non-intersecting tangent.
      if (intersects(X[bi], (Y[bi] = 0))) {
        let a = head;
        Y[bi] = Infinity;
        do {
          const ai = a.index;
          let y = Y[ai] + Math.sqrt(radius2 - (X[ai] - X[bi]) ** 2);
          if (y < Y[bi] && !intersects(X[bi], y)) Y[bi] = y;
          a = a.next;
        } while (a);
      }

      // Add b to the queue.
      const b = { index: bi, next: null };
      if (head === null) head = tail = b;
      else tail = tail.next = b;
    }

    return Y;
  }
</script>

<div
  class="chart-container"
  bind:offsetWidth={width}
  bind:offsetHeight={height}
>
  {#if width && height}
    <!-- Chart title -->
    <div
      class="chart-title"
      style="
        transform: translate({dimensions.marginLeft}px, {20}px);
      "
    >
      <div>Distribution of GE2020 Candidates <span class="light">by</span> Age</div>
      <div class="toggle">
        <Toggle
          bind:toggled={showNewcomers}
          small
          hideLabel
          toggledColor={"var(--purple)"}
        >
          <strong>Highlight Newcomers</strong>
        </Toggle>
      </div>
    </div>

    <!-- Tooltip -->
    {#if hoveredData}
      <div
        transition:fade={{ duration: 300 }}
        class="tooltip"
        style="
    transform: translate(calc({hoveredData.x +
          dimensions.marginLeft}px - 50%), calc({hoveredData.y +
          dimensions.marginTop +
          dimensions.boundedHeight / 2}px - 120%));
  "
      >
        <div class="tooltip-title">
          <span class="bold">{hoveredData.datum.candidatename}</span>, {hoveredData
            .datum.age}
        </div>
        <div class="tooltip-subtitle">
          {hoveredData.datum.party}, <span class="light">Terms:</span> {hoveredData.datum.noofterms}
        </div>
        <div class="tooltip-subtitle">
          <span class="light">Contesting</span> {hoveredData.datum.constituencycontesting}
        </div>
        <div class="tooltip-subtitle">
          {hoveredData.datum.occupation}
        </div>
      </div>
    {/if}

    <svg width={dimensions.width} height={dimensions.height} overflow="visible">
      <g
        transform={`translate(${dimensions.marginLeft}, ${dimensions.marginTop})`}
      >
        <!-- Beeswarm -->
        <g
          class="beeswarm"
          transform={`translate(0, ${dimensions.boundedHeight / 2})`}
        >
          <!-- Axis -->
          <AxisSwarm
            {dimensions}
            dimension={"x"}
            scale={xScale}
            drawGrid={true}
          />

          <g class="swarm-circles">
            {#each swarmedData as d}
              <!-- svelte-ignore a11y-mouse-events-have-key-events -->
              <circle
                on:mouseover={(e) => {
                  hoveredData = d;
                }}
                on:mouseout={(e) => {
                  hoveredData = null;
                }}
                cx={d.x}
                cy={d.y}
                r={radius}
                fill={isMale(d.datum)
                  ? "var(--green)"
                  : isFemale(d.datum)
                  ? "var(--purple)"
                  : "none"}
                fill-opacity={!showNewcomers ? 1 : isNew(d.datum) ? 1 : 0.3}
              />
            {/each}
          </g>
        </g>
      </g>
    </svg>
  {/if}
</div>

<style>
  .chart-container {
    position: relative;
    height: 70vh;
    max-width: 100%;
  }

  .chart-title {
    position: absolute;
    font-family: var(--font-serif);
    font-size: calc(20 / 16 * 1rem);
    font-weight: 700;
    /* text-shadow: var(--white-text-shadow); */
    text-align: left;
  }
  .light {
    color: var(--lightish-grey);
  }
  .toggle {
    font-size: calc(16 / 16 * 1rem);
    margin-top: 10px;
  }
  .toggle strong {
    font-weight: 400;
    margin-left: 10px;
    margin-top: -7px;
  }

  .tooltip {
    position: absolute;
    top: 0;
    left: 0;
    padding: 6px 10px;
    background: #fff;
    text-align: left;
    border: 1px solid #ddd;
    border-radius: 5px;
    pointer-events: none;
    font-family: var(--font-serif);
    font-size: calc(14 / 16 * 1rem);
  }
  .tooltip:before {
    content: "";
    position: absolute;
    bottom: 0;
    left: 50%;
    width: 12px;
    height: 12px;
    background: white;
    border: 1px solid #ddd;
    border-top-color: transparent;
    border-left-color: transparent;
    transform: translate(-50%, 50%) rotate(45deg);
    transform-origin: center center;
    z-index: 10;
  }
  .bold {
    font-weight: 700;
  }
  .tooltip-title {
    font-size: calc(20 / 16 * 1rem);
    margin-bottom: 5px;
    border-bottom: 1px solid #ddd;
  }
  .tooltip-subtitle {
    font-size: calc(14 / 16 * 1rem);
  }

  .swarm-circles circle {
    transition: all 0.3s ease-out;
  }
</style>
